// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
/**
 * Fonctions partagées par la liste des clients, qui ouvre un établissement, et
 * par la fiche du client, qui le corrige : tirage du mot de passe de
 * direction, valeurs initiales des formulaires, corps de requête, état d'un
 * groupe. Les dates et les messages d'erreur, communs à tout le dashboard,
 * vivent dans `utils/dates` et `utils/erreurs`.
 */
import { saveFile } from '../../utils/fichiers';

// Mot de passe de direction sans caractères ambigus (0/O, 1/l/I) : il est lu
// sur un écran puis retapé sur un téléphone, parfois par quelqu'un d'autre.
// Même alphabet que `staff_auth.generate_temp_password` côté serveur.
const PASSWORD_ALPHABET = 'abcdefghjkmnpqrstuvwxyzACDEFGHJKLMNPQRSTUVWXYZ23456789';
const PASSWORD_LENGTH = 16;
// 256 n'est pas un multiple de 54 : un simple modulo rendrait les 40 premiers
// caractères de l'alphabet plus probables que les autres. Les octets du bloc
// incomplet sont écartés plutôt que repliés.
const BYTE_LIMIT = Math.floor(256 / PASSWORD_ALPHABET.length) * PASSWORD_ALPHABET.length;

function randomChars(size) {
  const sortie = [];
  while (sortie.length < size) {
    const bytes = new Uint8Array(size);
    window.crypto.getRandomValues(bytes);
    for (let i = 0; i < bytes.length && sortie.length < size; i += 1) {
      if (bytes[i] >= BYTE_LIMIT) continue;
      sortie.push(PASSWORD_ALPHABET[bytes[i] % PASSWORD_ALPHABET.length]);
    }
  }
  return sortie.join('');
}

function isPasswordAcceptable(password, localEmail) {
  if (!/[0-9]/.test(password) || !/[a-z]/.test(password)) return false;
  // Le serveur refuse un mot de passe contenant la partie locale de l'adresse
  // (`staff_auth.check_password_policy`). Sur un tirage aléatoire c'est
  // pratiquement impossible, mais un 422 au moment de la pose coûterait cher.
  if (localEmail.length >= 3 && password.toLowerCase().includes(localEmail)) return false;
  return true;
}

export function generatePassword(email) {
  const local = String(email || '').split('@')[0].toLowerCase();
  let candidate = randomChars(PASSWORD_LENGTH);
  for (let attempt = 0; attempt < 32 && !isPasswordAcceptable(candidate, local); attempt += 1) {
    candidate = randomChars(PASSWORD_LENGTH);
  }
  return candidate;
}

// La veille de nuit se calcule sur le fuseau du site : sans les fuseaux
// d'outre-mer, un établissement réunionnais veillerait à l'heure de Paris.
export const TIMEZONES = [
  'Europe/Paris', 'Indian/Reunion', 'Indian/Mayotte', 'America/Guadeloupe',
  'America/Martinique', 'America/Cayenne', 'Pacific/Noumea', 'Pacific/Tahiti',
];

export const EMPTY_GROUP = {
  name: '', contact_name: '', contact_email: '', contact_phone: '',
  contract_start: '', notes: '',
};
export const EMPTY_SITE = {
  name: '', address: '', city: '', postal_code: '', country: 'France',
  timezone: 'Europe/Paris', server_id: '', owner_user_id: '',
};
export const EMPTY_DIRECTOR = { email: '', full_name: '' };

export const PLAUSIBLE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Corps de création : les champs laissés vides ne sont pas envoyés, sinon ils
 *  s'écrivent en base comme des chaînes vides. */
export function creationBody(formValues) {
  const body = {};
  Object.entries(formValues).forEach(([key, val]) => {
    if (typeof val === 'string' ? val.trim() !== '' : val != null) {
      body[key] = typeof val === 'string' ? val.trim() : val;
    }
  });
  return body;
}

/** Corps de modification : seuls les champs touchés partent.
 *  Le backend lit `null` comme « ne touche pas » ; une chaîne vide efface. */
export function updateBody(before, after, nonErasable = []) {
  const body = {};
  Object.entries(after).forEach(([key, val]) => {
    const newValue = typeof val === 'string' ? val.trim() : val;
    const old = before[key] ?? '';
    if (newValue === old) return;
    // `contract_start` est un datetime : une chaîne vide serait refusée en 422
    // au lieu d'effacer. L'API n'offre pas d'effacement, on ne le simule pas.
    if (newValue === '' && nonErasable.includes(key)) return;
    body[key] = newValue;
  });
  return body;
}

export function groupCities(org) {
  const cities = [...new Set((org.sites || []).map((s) => s.city).filter(Boolean))];
  if (!cities.length) return '';
  if (cities.length <= 2) return cities.join(', ');
  return `${cities.slice(0, 2).join(', ')} et ${cities.length - 2} autres`;
}

export function groupRooms(org) {
  return (org.sites || []).reduce((total, s) => total + (s.rooms || 0), 0);
}

/** État du client en un coup d'œil : un client sans établissement (création
 *  interrompue) ou un établissement fermé doit ressortir. */
export function groupState(org) {
  if (org.is_active === false) return { label: 'Client désactivé', color: 'default' };
  const sites = org.sites || [];
  if (!sites.length) return { label: 'Sans établissement', color: 'warning' };
  const activeItems = sites.filter((s) => s.is_active !== false).length;
  if (activeItems === 0) return { label: 'Tous désactivés', color: 'error' };
  if (activeItems < sites.length) return { label: `${activeItems} / ${sites.length} en service`, color: 'warning' };
  return { label: 'En service', color: 'success' };
}

/** Propose au navigateur d'enregistrer un objet JSON tel que l'API l'a rendu. */
export function downloadJson(object, fileName) {
  const blob = new Blob([JSON.stringify(object, null, 2)], { type: 'application/json' });
  saveFile(blob, fileName, { revocationDiffereeMs: 1000 });
}

/** Nom de fichier sûr tiré du nom d'un établissement. */
export function toFileName(text) {
  return String(text || 'etablissement')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    .toLowerCase() || 'etablissement';
}
